import { isAxiosError } from 'axios';
import { LogLevelNumbers, LogLevelNames, RootLogger } from 'loglevel';
import { useUserStore } from '@/state/user.state.ts';
import { sanitizeError } from '@/utils/sanitize-error';
import { traceIdManager } from '../services/traceIdManager.ts';
import { nextLogSequenceId } from './page-load.ts';
import type { User } from '@/types/auth.types.ts';
import type { WeavyLog, WeavyLogUser } from './types.ts';

const isError = (value: unknown): value is Error => {
  if (!value) return false;

  // Check for instanceof Error first (handles most standard cases)
  if (value instanceof Error) return true;

  // For cross-realm errors or custom error-like objects, check properties
  return Boolean(
    typeof value === 'object' &&
    'message' in value &&
    typeof value.message === 'string' &&
    'stack' in value &&
    typeof value.stack === 'string' &&
    ('name' in value ? typeof value.name === 'string' : true),
  );
};

let readSignedInUserId: () => string | undefined = () => undefined;

/**
 * Names the signed-in user on logs written before their Weave profile loads, or on a page that never loads it (the
 * tool page). The loaded profile's id wins once it is there, so the source must return that same Weave user id.
 */
export const setLogSignedInUserIdSource = (readUserId: () => string | undefined): void => {
  readSignedInUserId = readUserId;
};

const readSignedInUserIdForLog = (): string | undefined => {
  try {
    return readSignedInUserId();
  } catch {
    return undefined;
  }
};

const getUserDataToLog = (user: User | null): WeavyLogUser => {
  if (!user) {
    const signedInUserId = readSignedInUserIdForLog();
    return signedInUserId === undefined ? {} : { id: signedInUserId };
  }

  return {
    activeWorkspace: user.activeWorkspace,
    credits: user.activeWorkspace?.subscription?.credits,
    id: user.id,
    name: user.name,
    email: user.email,
    subscriptionType: user.subscriptionType,
    createdAt: user.createdAt,
  };
};

export const logFormatterMiddleware = (logger: RootLogger, appVersion: string): void => {
  const originalMethodFactory = logger.methodFactory;
  logger.methodFactory = (levelName: LogLevelNames, logLevel: LogLevelNumbers, loggerName: string | symbol) => {
    const logFunc = originalMethodFactory(levelName, logLevel, loggerName);

    return (...message: unknown[]) => {
      let error: string | object | undefined;
      let additionalInfo = {};
      const messageParts = message.filter((item: unknown) => {
        if (item instanceof Error || isError(item)) {
          // An AxiosError's `toJSON()` includes its request config: the bearer token and the request body
          // (a sign-in sends the Firebase refresh token). This log is printed with `console.error`, which
          // Datadog forwards.
          error = isAxiosError(item) ? sanitizeError(item) : item;
          return false;
        }
        if (typeof item === 'object' && !Array.isArray(item)) {
          additionalInfo = {
            ...additionalInfo,
            ...item,
          };

          return false;
        }

        return true;
      });

      const log: WeavyLog = {
        msg: messageParts.join(' '),
        level: levelName,
        context: String(loggerName),
        traceId: traceIdManager.getTraceId(),
        sequenceId:
          'sequenceId' in additionalInfo && typeof additionalInfo.sequenceId === 'number'
            ? additionalInfo.sequenceId
            : nextLogSequenceId(),
        userData: getUserDataToLog(useUserStore.getState().user),
        appVersion,
        additionalInfo,
        error,
        location: window.location.toString(),
      };

      logFunc(JSON.stringify(log));
    };
  };
  logger.setLevel(logger.getLevel());
};
